import { useState, useCallback, useMemo } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton } from '@devkit/ui';
import { Target, RefreshCw } from 'lucide-react';
import { trackToolExecute, trackCopy, trackClear } from '@devkit/analytics';

interface Match {
  text: string;
  index: number;
  groups: string[];
}

export default function RegexTesterPage() {
  const [pattern, setPattern] = useState('');
  const [flags, setFlags] = useState({ g: true, i: false, m: false, s: false, u: false });
  const [testString, setTestString] = useState('Hello World! This is a test string.\nEmail: test@example.com\nPhone: 123-456-7890');
  const [matches, setMatches] = useState<Match[]>([]);
  const [replacePattern, setReplacePattern] = useState('');
  const [replaceResult, setReplaceResult] = useState('');
  const [error, setError] = useState<string | null>(null);

  const flagString = useMemo(() => {
    return Object.entries(flags)
      .filter(([, enabled]) => enabled)
      .map(([flag]) => flag)
      .join('');
  }, [flags]);

  const toggleFlag = (flag: keyof typeof flags) => {
    setFlags((prev) => ({ ...prev, [flag]: !prev[flag] }));
  };

  const testRegex = useCallback(() => {
    setError(null);
    setMatches([]);
    setReplaceResult('');

    if (!pattern.trim()) {
      return;
    }

    try {
      const regex = new RegExp(pattern, flagString);
      const foundMatches: Match[] = [];
      
      if (flags.g) {
        let match;
        while ((match = regex.exec(testString)) !== null) {
          foundMatches.push({
            text: match[0],
            index: match.index,
            groups: match.slice(1),
          });
          if (!match[0].length) regex.lastIndex++;
        }
      } else {
        const match = regex.exec(testString);
        if (match) {
          foundMatches.push({
            text: match[0],
            index: match.index,
            groups: match.slice(1),
          });
        }
      }

      setMatches(foundMatches);
      trackToolExecute('regex-tester', 'test');
    } catch (e) {
      setError((e as Error).message);
    }
  }, [pattern, flagString, flags.g, testString]);

  const replace = useCallback(() => {
    setError(null);

    if (!pattern.trim()) {
      return;
    }

    try {
      const regex = new RegExp(pattern, flagString);
      setReplaceResult(testString.replace(regex, replacePattern));
      trackToolExecute('regex-tester', 'replace');
    } catch (e) {
      setError((e as Error).message);
    }
  }, [pattern, flagString, testString, replacePattern]);

  const clearAll = () => {
    setPattern('');
    setTestString('');
    setMatches([]);
    setReplaceResult('');
    setError(null);
    trackClear('regex-tester');
  };

  const highlightMatches = useMemo(() => {
    if (!matches.length) return testString;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    matches.forEach((match, i) => {
      if (match.index > lastIndex) {
        parts.push(testString.slice(lastIndex, match.index));
      }
      parts.push(
        <span key={i} className="bg-primary/30 text-primary-light rounded px-0.5">
          {match.text}
        </span>
      );
      lastIndex = match.index + match.text.length;
    });

    if (lastIndex < testString.length) {
      parts.push(testString.slice(lastIndex));
    }

    return parts;
  }, [matches, testString]);

  return (
    <ToolLayout slug="regex-tester">
      <Card>
        <CardContent className="p-6">
          {/* Pattern Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Regular Expression
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                placeholder="Enter regex pattern (e.g., \d+)"
                className="flex-1 px-4 py-2.5 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                value={flagString}
                readOnly
                className="w-20 px-4 py-2.5 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-400 text-center"
              />
            </div>
          </div>

          {/* Flags */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">Flags</label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'g', label: 'g', title: 'Global', description: 'Find all matches' },
                { key: 'i', label: 'i', title: 'Case Insensitive', description: 'Ignore case' },
                { key: 'm', label: 'm', title: 'Multiline', description: '^/$ match line start/end' },
                { key: 's', label: 's', title: 'Dotall', description: '. matches newlines' },
                { key: 'u', label: 'u', title: 'Unicode', description: 'Unicode support' },
              ].map((flag) => (
                <button
                  key={flag.key}
                  onClick={() => toggleFlag(flag.key as keyof typeof flags)}
                  className={`p-2 rounded-lg border transition-colors ${
                    flags[flag.key as keyof typeof flags]
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface text-slate-400 border-slate-700 hover:border-slate-600'
                  }`}
                  title={flag.description}
                >
                  <span className="font-mono text-sm">{flag.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Test String */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Test String
            </label>
            <textarea
              value={testString}
              onChange={(e) => setTestString(e.target.value)}
              placeholder="Enter text to test against..."
              className="w-full h-40 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 mb-4">
            <Button onClick={testRegex} className="gap-2">
              <Target className="w-4 h-4" />
              Test
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Results */}
          {matches.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-300">
                  Highlighted Matches ({matches.length})
                </label>
              </div>
              <div className="p-4 bg-surface border border-slate-700 rounded-lg font-mono text-sm whitespace-pre-wrap">
                {highlightMatches}
              </div>
            </div>
          )}

          {/* Match List */}
          {matches.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Match Details
              </label>
              <div className="space-y-2">
                {matches.map((match, i) => (
                  <div
                    key={i}
                    className="p-3 bg-surface border border-slate-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-400">Match {i + 1}</span>
                      <CopyButton text={match.text} onCopy={() => trackCopy('regex-tester')} className="text-xs" />
                    </div>
                    <code className="text-primary">{match.text}</code>
                    <p className="text-xs text-slate-500 mt-1">Index: {match.index}</p>
                    {match.groups.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-400 mb-1">Groups:</p>
                        {match.groups.map((group, gi) => (
                          <div key={gi} className="text-sm">
                            <span className="text-slate-500">${gi + 1}:</span>{' '}
                            <span className="text-secondary">{group || '(empty)'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Replace Section */}
          <div className="pt-4 border-t border-slate-700">
            <h3 className="text-sm font-medium text-slate-300 mb-3">Find & Replace</h3>
            <div className="mb-4">
              <label className="block text-sm text-slate-400 mb-2">Replace with</label>
              <input
                type="text"
                value={replacePattern}
                onChange={(e) => setReplacePattern(e.target.value)}
                placeholder="Replacement pattern (e.g., $1)"
                className="w-full px-4 py-2.5 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary"
              />
            </div>
            <Button variant="secondary" onClick={replace} className="gap-2 mb-4">
              <RefreshCw className="w-4 h-4" />
              Replace All
            </Button>
            {replaceResult && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Result
                </label>
                <div className="p-4 bg-surface border border-slate-700 rounded-lg font-mono text-sm whitespace-pre-wrap">
                  {replaceResult}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
